import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { ApiResource } from "@/lib/api/types"
import type {
  Issue,
  IssueFilters,
  IssueOutcome,
  IssuePage,
  IssuePerson,
  IssueSeverity,
  RaiseIssueInput,
} from "@/types/issue"

export const issueQueries = {
  list: (filters: IssueFilters = {}) =>
    queryOptions({
      queryKey: apiKey("/issues", filters),
      queryFn: async ({ signal }) =>
        await apiFetch<IssuePage>("/issues", {
          params: { ...filters, open: filters.open ? 1 : undefined },
          signal,
        }),
    }),

  assignees: () =>
    queryOptions({
      queryKey: apiKey("/issues/assignees"),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<IssuePerson[]>>(
          "/issues/assignees",
          { signal }
        )

        return data
      },
    }),

  detail: (issueId: string) =>
    queryOptions({
      queryKey: apiKey(`/issues/${issueId}`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<Issue>>(
          `/issues/${issueId}`,
          { signal }
        )

        return data
      },
    }),
}

function settle(queryClient: QueryClient, issue: Issue) {
  queryClient.setQueryData(apiKey(`/issues/${issue.id}`), issue)
}

async function send(path: string, method: "POST" | "PUT", body: unknown) {
  const { data } = await apiFetch<ApiResource<Issue>>(path, {
    method,
    body,
  })

  return data
}

export function useRaiseIssue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: RaiseIssueInput) =>
      await send("/issues", "POST", input),
    onSuccess: (issue) => settle(queryClient, issue),
  })
}

export function useMoveIssue(issueId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (step: "investigation" | "mitigation") =>
      await send(`/issues/${issueId}/${step}`, "POST", {}),
    onSuccess: (issue) => settle(queryClient, issue),
  })
}

export function useAcceptIssue(issueId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { severity: IssueSeverity }) =>
      await send(`/issues/${issueId}/acceptance`, "POST", input),
    onSuccess: (issue) => settle(queryClient, issue),
  })
}

export function useMergeIssue(issueId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { into_id: string }) =>
      await send(`/issues/${issueId}/merge`, "POST", input),
    onSuccess: (issue) => settle(queryClient, issue),
  })
}

export function useCloseIssue(issueId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { outcome: IssueOutcome; resolution: string }) =>
      await send(`/issues/${issueId}/closure`, "POST", input),
    onSuccess: (issue) => settle(queryClient, issue),
  })
}

export function useAssignIssue(issueId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (assigneeId: string | null) =>
      await send(`/issues/${issueId}/assignee`, "PUT", {
        assignee_id: assigneeId,
      }),
    onSuccess: (issue) => settle(queryClient, issue),
  })
}

export function useSetIssueReviewDate(issueId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (reviewOn: string | null) =>
      await send(`/issues/${issueId}/review-date`, "PUT", {
        review_on: reviewOn,
      }),
    onSuccess: (issue) => settle(queryClient, issue),
  })
}

export function useCommentOnIssue(issueId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: string) =>
      await send(`/issues/${issueId}/comments`, "POST", { body }),
    onSuccess: (issue) => settle(queryClient, issue),
  })
}

export function useDescribeIssue(issueId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (description: string) =>
      await send(`/issues/${issueId}/description`, "PUT", { description }),
    onSuccess: (issue) => settle(queryClient, issue),
  })
}
